import { DIRECTIONS, cellKey, stepCell, type Cell, type Direction } from './types';

export class Grid {
  readonly width: number;
  readonly height: number;

  constructor(width: number, height: number) {
    this.width = width;
    this.height = height;
  }

  isInside(cell: Cell): boolean {
    return cell.x >= 0 && cell.y >= 0 && cell.x < this.width && cell.y < this.height;
  }

  neighbors(cell: Cell, isBlocked: (cell: Cell) => boolean): Cell[] {
    const result: Cell[] = [];
    for (const direction of DIRECTIONS) {
      const next = stepCell(cell, direction);
      if (this.isInside(next) && !isBlocked(next)) result.push(next);
    }
    return result;
  }

  flood(start: Cell, isBlocked: (cell: Cell) => boolean): Set<string> {
    const reached = new Set<string>();
    if (!this.isInside(start) || isBlocked(start)) return reached;
    const queue: Cell[] = [start];
    reached.add(cellKey(start));
    for (let index = 0; index < queue.length; index += 1) {
      const current = queue[index];
      if (!current) continue;
      for (const neighbor of this.neighbors(current, isBlocked)) {
        const key = cellKey(neighbor);
        if (reached.has(key)) continue;
        reached.add(key);
        queue.push(neighbor);
      }
    }
    return reached;
  }

  canReach(start: Cell, goal: Cell, isBlocked: (cell: Cell) => boolean): boolean {
    return this.flood(start, isBlocked).has(cellKey(goal));
  }

  directionToAdjacent(from: Cell, to: Cell): Direction | null {
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    if (dx === 1 && dy === 0) return 'right';
    if (dx === -1 && dy === 0) return 'left';
    if (dx === 0 && dy === 1) return 'down';
    if (dx === 0 && dy === -1) return 'up';
    return null;
  }
}
